import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { faCheckToSlot } from '@fortawesome/pro-regular-svg-icons/faCheckToSlot';
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Col, type TableColumnsType } from 'antd';
import { PosCard } from 'pos-client/components/shared/Display/Card/PosCard.tsx';
import { PosTable } from 'pos-client/components/shared/Display/Table/PosTable.tsx';
import { useTableData } from 'pos-client/hooks/useTableData.ts';
import { CreateEditDropOffsModal } from 'pos-client/pages/locations/Modals/CreateEditDropOffsModal.tsx';
import {
  createTableFilters,
  filterByProperty,
  sortStrings,
  sortTimes,
  sortWeekdays,
} from 'pos-client/utils/dataTableHelpers.tsx';
import { clearSeconds } from 'pos-client/utils/dateHelpers.ts';
import { trpc } from 'pos-client/utils/trpc.ts';
import type { DropOffsModel } from 'pos-shared/models/openingHoursGroupSchema.ts';

export const DropOffs = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { data, isLoading } = trpc.getAllDropOffs.useQuery({
    id: id as string,
  });
  const tableData = useTableData(data, (x) => x.id || x.dayOfTheWeek);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalData, setModalData] = useState<
    Partial<DropOffsModel> | undefined
  >();

  const showModal = () => {
    setIsModalVisible(true);
  };

  const showEditModal = (data: DropOffsModel) => {
    setModalData(data);
    setIsModalVisible(true);
  };

  const handleOk = () => {
    setModalData(undefined);
    setIsModalVisible(false);
  };

  const handleCancel = () => {
    setModalData(undefined);
    setIsModalVisible(false);
  };

  const formattedTableData = tableData.map((obj) => ({ ...obj }));
  formattedTableData.forEach((element) => {
    element.time = clearSeconds(element.time);
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: TableColumnsType<any> = [
    {
      title: t('common.time.unit.day'),
      dataIndex: 'dayOfTheWeek',
      key: 'dayOfTheWeek',
      sorter: (a, b) => sortWeekdays<DropOffsModel>(a, b, 'dayOfTheWeek'),
      filters: createTableFilters(tableData, 'dayOfTheWeek'),
      onFilter: (value, record) =>
        filterByProperty(value, record, 'dayOfTheWeek'),
    },
    {
      title: t('locations.trading.startingTime'),
      dataIndex: 'time',
      key: 'time',
      sorter: (a, b) => sortTimes(a.time, b.time),
      filters: createTableFilters(formattedTableData, 'time'),
      onFilter: (value, record) => filterByProperty(value, record, 'time'),
    },
    {
      title: `${t('common.time.duration')} (${t('common.time.unit.hours')})`,
      dataIndex: 'length',
      key: 'length',
      sorter: (a, b) => a.length - b.length,
      filters: createTableFilters(tableData, 'length', false, true),
      onFilter: (value, record) => filterByProperty(value, record, 'length'),
    },
    {
      title: t('locations.trading.flexibility'),
      dataIndex: 'flexibility',
      key: 'flexibility',
      sorter: (a, b) => sortStrings<DropOffsModel>(a, b, 'flexibility'),
      filters: createTableFilters(tableData, 'flexibility'),
      onFilter: (value, record) =>
        filterByProperty(value, record, 'flexibility'),
    },
    {
      title: '',
      dataIndex: 'id',
      width: 10,
      render: (_, record) => (
        <Button size="small" onClick={() => showEditModal(record)}>
          {t('common.buttons.edit')}
        </Button>
      ),
    },
  ];

  return (
    <Col>
      <PosCard
        headerProps={{
          title: t('scheduling.slots.dropOffs'),
          icon: faCheckToSlot,
          action: (
            <Button
              onClick={showModal}
              icon={<FontAwesomeIcon icon={faPlus} />}
            >
              {t('common.buttons.addNew')}
            </Button>
          ),
        }}
      >
        <PosTable
          id="dropOffsDetailsTable"
          columns={columns}
          dataSource={tableData}
          isLoading={isLoading}
          fePagination
        />
        {isModalVisible && (
          <CreateEditDropOffsModal
            handleOk={handleOk}
            handleCancel={handleCancel}
            data={modalData}
          />
        )}
      </PosCard>
    </Col>
  );
};
